import { animationFrameScheduler, fromEvent, mapTo, merge, Observable, of, tap } from 'rxjs';
import { debounceTime, delay, distinctUntilChanged, map, startWith, takeWhile, throttleTime } from 'rxjs/operators';
import { ContentObserver } from '@angular/cdk/observers';
import { CdkScrollable } from '@angular/cdk/overlay';
import {
  booleanAttribute,
  DestroyRef,
  Directive,
  effect,
  ElementRef,
  EventEmitter,
  inject,
  input,
  OnInit,
  Output,
} from '@angular/core';

import { tweenTo } from '@shared/operators/tween-to.operator';
import { getPositionOfChildElement } from '@shared/utilities/dom.utilities';
import { LifecycleHooks } from '@shared/services/lifecycle-hooks.service';
import { NavBarService } from '@home/shared/modules/nav-bar/nav-bar.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Directive({
  selector: '[zefScrollable]',
  providers: [LifecycleHooks],
  standalone: false,
})
export class ScrollScrollable extends CdkScrollable implements OnInit {
  private co = inject(ContentObserver);

  private nbs = inject(NavBarService);

  private dr = inject(DestroyRef);

  @Output()
  readonly canScrollY = new EventEmitter<boolean>();

  readonly scrollHidesNavBar = input(false, { transform: booleanAttribute });

  get nativeElement(): HTMLElement {
    return this.elementRef.nativeElement;
  }

  get scrollTop(): number {
    return this.nativeElement.scrollTop;
  }

  set scrollTop(scrollTop: number) {
    this.nativeElement.scrollTop = scrollTop;
  }

  constructor() {
    super();

    effect(() => {
      const hideScrollBar = this.scrollHidesNavBar();

      if (hideScrollBar) {
        fromEvent(this.nativeElement, 'scroll', { passive: true })
          .pipe(
            throttleTime(100, animationFrameScheduler, { trailing: true }),
            takeWhile(() => this.scrollHidesNavBar()),
            takeUntilDestroyed(this.dr),
          )
          .subscribe({
            next: () => this.nbs.scrollHideHeight.set(this.scrollTop),
            complete: () => this.nbs.scrollHideHeight.set(0),
          });
      } else {
        this.nbs.scrollHideHeight.set(0);
      }
    });
  }

  ngOnInit() {
    merge(fromEvent(window, 'resize', { passive: true }), this.co.observe(this.elementRef.nativeElement))
      .pipe(
        startWith(void 0),
        debounceTime(10, animationFrameScheduler),
        map(() => {
          const el = this.elementRef.nativeElement;

          return el.scrollHeight > el.clientHeight;
        }),
        distinctUntilChanged(),
      )
      .subscribe((canScrollY) => this.canScrollY.emit(canScrollY));

    super.ngOnInit();
  }

  scrollToBottom(): void {
    this.scrollTo({ bottom: 0 });
  }

  scrollToElement(
    element?: HTMLElement | ElementRef<HTMLElement>,
    options?: { duration?: number; offset?: number },
  ): Observable<void> {
    const targetElement = element instanceof HTMLElement ? element : element?.nativeElement;
    options ??= {};
    options.duration ??= 0;
    options.offset ??= 0;

    const currentTop = this.elementRef.nativeElement.scrollTop;
    const top = element ? getPositionOfChildElement(targetElement, this.elementRef.nativeElement) - options.offset : 0;

    if (currentTop !== top && !options.duration) {
      this.scrollTo({ top });
    }

    if (currentTop === top || !options.duration) {
      return of(void 0);
    }

    return of([currentTop, top]).pipe(
      tweenTo(500, (nextTop) => this.scrollTo({ top: nextTop })),
      delay(200),
      tap(() => {
        const nextTop = targetElement
          ? getPositionOfChildElement(targetElement, this.elementRef.nativeElement) - options.offset
          : 0;

        if (nextTop !== top) {
          this.scrollTo({ top: nextTop });
        }
      }),
      mapTo(void 0),
    );
  }
}
